import { useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import md5 from 'md5';
import * as io from 'socket.io-client';
import { ManagerOptions, Socket } from 'socket.io-client';
import * as authApi from '@apis/auth';
import { Roles } from '@consts/role';
import { CardMessageContent, ChatMessageType } from '@models/chatMessage';
import { useAppSocketStore } from '@stores/appSocketStore';
import { useAppUserSessionStore } from '@stores/appUserSessionStore';
import { useAuthStore } from '@stores/authStore';
import { getUserDeviceId } from '@utils/string';
import { API_URL } from '@variables';

export interface NewAppSocketPayload {
  roomId: string;
  uuid: string;
  senderId: string;
}

export interface ReadChatMessagePayload {
  roomId: string;
  readerId: string;
}

export type ChatMessagePayload =
  | ChatTextMessagePayload
  | ChatImagesMessagePayload
  | ChatFileMessagePayload
  | ChatCardMessagePayload;

interface ChatTextMessagePayload extends NewAppSocketPayload {
  type: ChatMessageType.TEXT;
  content: string;
}

interface ChatImagesMessagePayload extends NewAppSocketPayload {
  type: ChatMessageType.IMAGE;
  content: { data: Buffer[] };
}

interface ChatFileMessagePayload extends NewAppSocketPayload {
  type: ChatMessageType.FILE;
  content: { data: Buffer; originName: string };
}

interface ChatCardMessagePayload extends NewAppSocketPayload {
  type: ChatMessageType.CARD;
  content: CardMessageContent;
  lastMessage: string;
}

export interface AppSocketPayload {
  productId?: string;
  roomId?: string;
  sender: {
    userId: string;
    role: Roles;
  };
}

export interface AppEventPayload {
  type: 'user-agent' | 'user-info' | 'login' | 'logout' | 'view' | 'click' | 'behavior';
  md5?: string;
  data?: any;
}

export interface AppTokenPayload {
  deviceId: string;
  fcmToken: string;
  iosOriginToken?: string;
  userId?: string;
  lastLoginType?: string;
}

export interface ConnectOptions {
  deviceId: string;
  [key: string]: any;
}

export interface AppSocketFileData {
  type: string;
  file: File;
  option?: {
    sourceName?: string;
  };
}
export interface AppSocketFile extends AppSocketPayload {
  data: AppSocketFileData;
}

export interface AutoAssigningProgressPayload {
  jobId: string;
  step: string;
  message: string;
  progress: number; // 0-100
  status: 'processing' | 'completed' | 'error';
}

export interface AutoAssigningStartPayload {
  productId: string;
  optionId: string;
  startDate: string;
  scheduleIds?: string[];
  minGroupSize?: number;
  maxGroupSize?: number;
}

export interface AutoAssigningResultPayload {
  jobId: string;
  groups: any[];
  unassigned?: {
    scheduleIds: string[];
    details: {
      scheduleId: string;
      name: string;
      reason: string;
    }[];
  };
  analysisReport: {
    groupingStrategy: string;
    groupsFormed: number;
    assignedParticipants: number;
    unassignedParticipants: number;
    unassignedAnalysis: string;
    reviewRequired: {
      type: string;
      target: string;
      issue: string;
      recommendation: string;
    }[];
    summary: {
      totalChildren: number;
      assignedChildren: number;
      unassignedChildren: number;
      groupsFormed: number;
      friendGroupsKeptTogether: number;
      sameGuardianGroupsKeptTogether: number;
    };
  };
}

export interface BulkGroupingStartPayload {
  force?: boolean;
}

export interface BulkGroupingProgressPayload {
  jobId: string;
  runId?: string;
  status: 'processing' | 'completed' | 'error' | 'skipped';
  message: string;
  progress: number;
  processedCount: number;
  totalCount: number;
  skippedCount?: number;
  failedCount?: number;
  currentOption?: {
    productName?: string;
    optionName?: string;
    startDate?: string;
    mode?: 'full' | 'incremental' | 'skipped';
  };
}

export interface BulkGroupingCompletedPayload {
  jobId: string;
  runId?: string;
  result: 'OK' | 'SKIPPED';
  message: string;
  routingSummary?: {
    full: number;
    incremental: number;
    skipped: number;
  };
  summary?: {
    processedScheduleCount?: number;
    skippedScheduleCount?: number;
    failedScheduleCount?: number;
  };
}

export interface BulkGroupingErrorPayload {
  jobId: string;
  error: string;
}

type EventFunctionKeys =
  | 'connect'
  | 'disconnect'
  | 'send_chat_message'
  | 'read_chat_message'
  | 'payment_result'
  | 'chat_message_status'
  | 'upload_excel_status'
  | 'auto_assigning_progress'
  | 'auto_assigning_completed'
  | 'auto_assigning_error'
  | 'bulk_grouping_progress'
  | 'bulk_grouping_completed'
  | 'bulk_grouping_error';

export class AppSocket {
  connecting: boolean = false;
  connected: boolean = false;
  host: string;
  path: string;
  socket?: Socket;
  opts?: Partial<ManagerOptions>;
  eventFunctions: { [key: string]: { [key: string]: any } } = {
    connect: {},
    disconnect: {},
    send_chat_message: {},
    read_chat_message: {},
    payment_result: {},
    chat_message_status: {},
    upload_excel_status: {},
    sync_naver_commerce_products_status: {},
    auto_assigning_progress: {},
    auto_assigning_completed: {},
    auto_assigning_error: {},
    bulk_grouping_progress: {},
    bulk_grouping_completed: {},
    bulk_grouping_error: {},
  };

  constructor(host: string, path?: string, opts?: Partial<ManagerOptions>) {
    this.host = host;
    this.opts = opts;

    if (path) {
      this.path = path;
    } else {
      this.path = '/socket.io';
    }
  }

  connect = (options?: ConnectOptions) => {
    this.connecting = true;
    this.socket = io.connect(this.host, {
      path: this.path,
      query: options,
      reconnection: true,
      ...this.opts,
    });

    this.socket.on('connect', () => {
      this.connected = true;
      Object.values(this.eventFunctions['connect']).forEach((func) => func());
    });

    this.socket.on('disconnect', () => {
      this.connected = false;
      Object.values(this.eventFunctions['disconnect']).forEach((func) => func());
    });

    this.socket.on('send_chat_message', (response: object) => {
      Object.values(this.eventFunctions['send_chat_message']).forEach((func) => func(response));
    });

    this.socket.on('read_chat_message', (response: object) => {
      Object.values(this.eventFunctions['read_chat_message']).forEach((func) => func(response));
    });

    this.socket.on('upload_excel_status', (response: object) => {
      Object.values(this.eventFunctions['upload_excel_status']).forEach((func) => func(response));
    });

    this.socket.on('auto_assigning_progress', (response: AutoAssigningProgressPayload) => {
      Object.values(this.eventFunctions['auto_assigning_progress']).forEach((func) => func(response));
    });

    this.socket.on('auto_assigning_completed', (response: AutoAssigningResultPayload) => {
      Object.values(this.eventFunctions['auto_assigning_completed']).forEach((func) => func(response));
    });

    this.socket.on('auto_assigning_error', (response: { jobId: string; error: string }) => {
      Object.values(this.eventFunctions['auto_assigning_error']).forEach((func) => func(response));
    });

    this.socket.on('bulk_grouping_progress', (response: BulkGroupingProgressPayload) => {
      Object.values(this.eventFunctions['bulk_grouping_progress']).forEach((func) => func(response));
    });

    this.socket.on('bulk_grouping_completed', (response: BulkGroupingCompletedPayload) => {
      Object.values(this.eventFunctions['bulk_grouping_completed']).forEach((func) => func(response));
    });

    this.socket.on('bulk_grouping_error', (response: BulkGroupingErrorPayload) => {
      Object.values(this.eventFunctions['bulk_grouping_error']).forEach((func) => func(response));
    });
  };

  disconnect = () => {
    if (this.socket && this.socket.connected) {
      this.socket.disconnect();
    } else {
      throw new Error('There is no connection');
    }
  };

  emit = (event: string, data: object) => {
    if (this.socket) {
      this.socket.emit(event, data);
    } else {
      throw new Error('There is no connection');
    }
  };

  addSocketEventListener = (event: EventFunctionKeys, key: string, callback: (data: any) => void) => {
    this.eventFunctions[event][key] = callback;
  };

  removeSocketEventListener = (event: EventFunctionKeys, key: string) => {
    try {
      delete this.eventFunctions[event][key];
      // eslint-disable-next-line no-empty
    } catch {}
  };
}

const appSocket = new AppSocket(API_URL, undefined, { transports: ['websocket'] });

export const useAppSocket = () => {
  const appSocketStore = useAppSocketStore();
  const authStore = useAuthStore();
  const sessionStore = useAppUserSessionStore();

  useEffect(() => {
    if (!appSocket.connecting) {
      appSocket.connect({
        userId: authStore.user?.id,
        deviceId: getUserDeviceId(),
        sessionId: sessionStore.getSessionId(),
        product: 'partner',
      });
      appSocketStore.setAppSocket(appSocket);
      appSocket.addSocketEventListener('connect', 'app-connect', () => {
        appSocketStore.setConnected(true);
        sendEvent({ type: 'user-agent', data: { userAgent: navigator.userAgent } }, appSocket);

        authApi
          .getMe()
          .then((response) => {
            authStore.setUser(response.data);
            if (response.data) {
              if (sessionStore.step === 0) {
                sendEvent({ type: 'login', data: authStore.user }, appSocket);
              } else {
                sendEvent({ type: 'user-info', data: response.data }, appSocket);
              }
            }
          })
          .catch(() => {});
        sessionStore.setActionData({ actionType: 'enter', to: window.location.pathname, from: document.referrer });
      });
      appSocket.addSocketEventListener('disconnect', 'app-disconnect', () => {
        appSocketStore.setConnected(false);
      });
      appSocketStore.setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendEvent = (data: AppEventPayload, socket?: AppSocket) => {
    const eventData = { ...data, md5: md5(JSON.stringify(data)), isNativeApp: false, isMobile };
    (socket || appSocketStore.appSocket)?.emit('event', eventData);
  };

  const sendMessage = (data: ChatMessagePayload) => {
    appSocketStore.appSocket?.emit('send_chat_message', data);
  };

  const readMessage = (data: Omit<ReadChatMessagePayload, 'partnerId'>) => {
    appSocketStore.appSocket?.emit('read_chat_message', data);
  };

  const enterRoom = (data: Pick<NewAppSocketPayload, 'roomId'>) => {
    appSocketStore.appSocket?.emit('join_chat_room', data);
  };

  const leaveRoom = (data: Pick<NewAppSocketPayload, 'roomId'>) => {
    appSocketStore.appSocket?.emit('exit_chat_room', data);
  };

  const updateAppToken = (data: AppTokenPayload) => {
    appSocketStore.appSocket?.emit('app_token', data);
  };

  const uploadExcelFile = (data: AppSocketFile) => {
    appSocketStore.appSocket?.emit('upload_excel_file', data);
  };

  const startAutoAssigning = (data: AutoAssigningStartPayload) => {
    appSocketStore.appSocket?.emit('start_auto_assigning', data);
  };

  const startBulkGrouping = (data?: BulkGroupingStartPayload) => {
    appSocketStore.appSocket?.emit('start_bulk_grouping', data || {});
  };

  const addSocketEventListener = (
    event: EventFunctionKeys,
    key: string,
    callback: (data: any) => void,
  ) => {
    appSocketStore.appSocket?.addSocketEventListener(event, key, callback);
  };

  const removeSocketEventListener = (event: EventFunctionKeys, key: string) => {
    appSocketStore.appSocket?.removeSocketEventListener(event, key);
  };

  return {
    sendEvent,
    sendMessage,
    readMessage,
    enterRoom,
    leaveRoom,
    updateAppToken,
    uploadExcelFile,
    startAutoAssigning,
    startBulkGrouping,
    addSocketEventListener,
    removeSocketEventListener,
    socket: appSocket,
    connected: appSocketStore.connected,
  };
};
